import React, { useState, useEffect, useRef } from 'react';
import {
  FiEye, FiEyeOff, FiSave, FiCheckCircle, FiAlertCircle, FiRefreshCw,
  FiLink, FiTrash2, FiAlertTriangle, FiInfo, FiPlus,
} from 'react-icons/fi';
import {
  buscarConfigDocs, salvarConfigDocs, removerConfigDocs,
  testarConexaoDocs, compararComDocs, vincularNoDocs, criarAquiDoDocs, criarNoDocs,
  listarLigados, desvincularNoDocs,
} from '../../services/integracaoDocs';
import styles from './Configuracoes.module.css';

/* ── Integração com o Docs (gestão de documentos) ──
   A chave é credencial: some da tela assim que é salva (o backend guarda
   cifrada e nunca devolve). Aqui a tela só sabe se existe ou não.

   Nada liga cadastro sozinho: "Comparar" não escreve nada dos dois lados,
   e o vínculo só sai daqui quando alguém marca as linhas e clica em
   "Vincular selecionados" -- inclusive os casados, que já vêm marcados
   por conveniência mas continuam esperando o clique. Nenhuma tela aqui
   apaga nada: integração não exclui cadastro. */

function primeiro(...valores) {
  return valores.find(v => v !== undefined && v !== null && v !== '') ?? null;
}

/* SÓ `casados` e `conferir` vêm aninhados -- é lá que cada linha tem os
   dois lados. A nomenclatura é da perspectiva do Docs, que é quem responde:
     item.externo = o cadastro DAQUI (app.essensials), como foi enviado
     item.local   = o cadastro DO DOCS
   Para os grupos de um lado só, use extrairRegistro (logo abaixo). */
export function extrairLinha(item, nossos) {
  const aqui = item?.externo || {};
  const docs = item?.local   || {};
  const meu  = aqui?.id ? nossos?.[aqui.id] : null;

  const idNosso = primeiro(aqui?.id);
  const idDocs  = primeiro(docs?.id);

  return {
    idNosso,
    idDocs,
    codigoAqui: primeiro(aqui?.codigo, meu?.codigo) || '—',
    codigoDocs: primeiro(docs?.codigo) || '—',
    nomeAqui:   primeiro(aqui?.nome, meu?.nome) || '—',
    nomeDocs:   primeiro(docs?.nome) || '—',
    cnpjAqui:   primeiro(aqui?.cnpj),
    cnpjDocs:   primeiro(docs?.cnpj),
    motivo:     primeiro(item?.motivo),
    jaVinculado: !!item?.ja_vinculado,
    /* só dá pra vincular quem tem os dois lados identificados */
    vinculavel: !!(idNosso && idDocs),
  };
}

/* `so_no_docs` e `so_no_sistema_externo` vêm PLANOS: são os registros crus
   de um lado só, sem nada pra aninhar (não existe o outro lado). Ler esses
   com extrairLinha é o que deixava "Só no Docs" todo com "—". */
export function extrairRegistro(item) {
  return {
    id:     primeiro(item?.id),
    codigo: primeiro(item?.codigo) || '—',
    nome:   primeiro(item?.nome) || '—',
    cnpj:   primeiro(item?.cnpj),
  };
}

/* Par no formato que o Docs espera em POST /integracao/clientes/vincular:
   `cliente_id` é o id do cliente NO DOCS (item.local.id) e `id_externo` é
   o NOSSO id (item.externo.id), que é o valor gravado lá -- a única coluna
   que a chamada escreve. Inverter os dois grava lixo no Docs. */
export function montarPar(linha) {
  return { cliente_id: linha.idDocs, id_externo: String(linha.idNosso) };
}

function chaveLinha(linha) {
  return `${linha.idNosso || '?'}|${linha.idDocs || '?'}`;
}

function formatarCnpj(valor) {
  const d = String(valor || '').replace(/\D/g, '');
  if (d.length !== 14) return valor || '—';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function formatarDataHora(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* grupo com checkbox (casados e conferir) */
function TabelaVinculavel({ linhas, selecionados, onAlternar, onMarcarTodos, destaque, mostrarMotivo }) {
  const vinculaveis = linhas.filter(l => l.vinculavel);
  const todosMarcados = vinculaveis.length > 0 && vinculaveis.every(l => selecionados.has(chaveLinha(l)));

  return (
    <div className={styles.docsTabelaWrap}>
      <table className={styles.docsTabela}>
        <thead>
          <tr>
            <th className={styles.docsColCheck}>
              <input
                type="checkbox"
                checked={todosMarcados}
                disabled={vinculaveis.length === 0}
                onChange={() => onMarcarTodos(vinculaveis, !todosMarcados)}
                title={todosMarcados ? 'Desmarcar todos' : 'Marcar todos'}
              />
            </th>
            <th>Código aqui</th>
            <th>Código no Docs</th>
            <th>Nome aqui</th>
            <th>Nome no Docs</th>
            <th>CNPJ</th>
            {mostrarMotivo && <th>Por que conferir</th>}
          </tr>
        </thead>
        <tbody>
          {linhas.map(linha => {
            const chave = chaveLinha(linha);
            return (
              <tr key={chave} className={destaque ? styles.docsLinhaConferir : undefined}>
                <td className={styles.docsColCheck}>
                  <input
                    type="checkbox"
                    checked={selecionados.has(chave)}
                    disabled={!linha.vinculavel}
                    onChange={() => onAlternar(linha)}
                    title={linha.vinculavel ? 'Selecionar para vincular' : 'Sem os dois ids — não dá para vincular'}
                  />
                </td>
                <td className={styles.docsCodigo}>{linha.codigoAqui}</td>
                <td className={styles.docsCodigo}>{linha.codigoDocs}</td>
                <td>
                  {linha.nomeAqui}
                  {linha.jaVinculado && <span className={styles.docsBadgeLigado}>já ligado</span>}
                </td>
                <td>{linha.nomeDocs}</td>
                <td className={styles.docsCnpj}>{formatarCnpj(linha.cnpjAqui || linha.cnpjDocs)}</td>
                {mostrarMotivo && (
                  <td className={styles.docsMotivo}>{linha.motivo || 'Código e CNPJ não batem'}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Grupo só informativo (so_no_docs / so_no_sistema_externo): registros
   planos de um lado só -- mostra a lista e nada mais, sem botão de ação.
   Criar o cadastro que falta do outro lado é o passo seguinte do projeto
   e o endpoint não existe. */
function TabelaInformativa({ linhas, selecionados, onAlternar }) {
  const selecionavel = !!onAlternar;
  return (
    <div className={styles.docsTabelaWrap}>
      <table className={styles.docsTabela}>
        <thead>
          <tr>
            {selecionavel && <th className={styles.docsColCheck}></th>}
            <th>Código</th>
            <th>Nome</th>
            <th>CNPJ</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, i) => (
            <tr key={linha.id || i}>
              {selecionavel && (
                <td className={styles.docsColCheck}>
                  <input
                    type="checkbox"
                    checked={selecionados.has(String(linha.id))}
                    disabled={!linha.id}
                    onChange={() => onAlternar(String(linha.id))}
                  />
                </td>
              )}
              <td className={styles.docsCodigo}>{linha.codigo}</td>
              <td>{linha.nome}</td>
              <td className={styles.docsCnpj}>{formatarCnpj(linha.cnpj)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Grupo({ titulo, descricao, quantidade, children, tom }) {
  const [aberto, setAberto] = useState(true);
  return (
    <div className={`${styles.docsGrupo} ${tom ? styles[tom] : ''}`}>
      <button type="button" className={styles.docsGrupoHeader} onClick={() => setAberto(a => !a)}>
        <span className={styles.docsGrupoTitulo}>{titulo}</span>
        <span className={styles.docsGrupoContagem}>{quantidade}</span>
        <span className={styles.docsGrupoDesc}>{descricao}</span>
        <span className={styles.docsGrupoSeta}>{aberto ? '▾' : '▸'}</span>
      </button>
      {aberto && quantidade > 0 && children}
      {aberto && quantidade === 0 && <p className={styles.docsVazio}>Nada neste grupo.</p>}
    </div>
  );
}

function DocsTab() {
  const [config, setConfig]           = useState(null);
  const [chave, setChave]             = useState('');
  const [mostrarChave, setMostrarChave] = useState(false);
  const [carregando, setCarregando]   = useState(true);
  const [salvando, setSalvando]       = useState(false);
  const [testando, setTestando]       = useState(false);
  const [comparando, setComparando]   = useState(false);
  const [vinculando, setVinculando]   = useState(false);
  const [erro, setErro]               = useState('');
  const [sucesso, setSucesso]         = useState('');
  const [teste, setTeste]             = useState(null);
  const [comparacao, setComparacao]   = useState(null);
  const [selecionados, setSelecionados] = useState(new Set());
  const [selecionadosDocs, setSelecionadosDocs] = useState(new Set()); // ids do Docs p/ cadastrar aqui
  const [selecionadosNossos, setSelecionadosNossos] = useState(new Set()); // ids daqui p/ criar no Docs
  const [criandoAqui, setCriandoAqui] = useState(false);
  const [criandoLa, setCriandoLa] = useState(false);
  const [ligados, setLigados] = useState(null);      // null = ainda não pedido
  const [carregandoLigados, setCarregandoLigados] = useState(false);

  /* O aviso (erro ou sucesso) fica no topo da aba, e os botões ficam no
     fim das listas. Quem clica lá embaixo não enxergava o resultado --
     ficava sem saber se a ação tinha acontecido. Depois de cada ação,
     traz o aviso pra vista. */
  const avisoRef = useRef(null);
  useEffect(() => {
    if (erro || sucesso) avisoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [erro, sucesso]);

  useEffect(() => {
    buscarConfigDocs()
      .then(setConfig)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  function limparAvisos() { setErro(''); setSucesso(''); }

  async function handleSalvarChave() {
    if (!chave.trim()) { setErro('Informe a chave de integração gerada no Docs.'); return; }
    setSalvando(true); limparAvisos();
    try {
      await salvarConfigDocs({ chave: chave.trim() });
      setChave('');                       // a chave some da tela assim que é salva
      setMostrarChave(false);
      setTeste(null);
      setConfig(await buscarConfigDocs());
      setSucesso('Chave salva. Use "Testar conexão" para conferir.');
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  }

  async function handleRemoverChave() {
    if (!window.confirm('Remover a chave de integração? O app deixa de falar com o Docs até você salvar outra. Nada é apagado no Docs.')) return;
    setSalvando(true); limparAvisos();
    try {
      await removerConfigDocs();
      setTeste(null); setComparacao(null); setSelecionados(new Set());
      setConfig(await buscarConfigDocs());
      setSucesso('Chave removida deste app.');
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  }

  async function handleTestar() {
    setTestando(true); limparAvisos(); setTeste(null);
    try {
      setTeste(await testarConexaoDocs());
      setConfig(await buscarConfigDocs());
    } catch (e) { setErro(e.message); } finally { setTestando(false); }
  }

  async function handleComparar() {
    setComparando(true); limparAvisos();
    try {
      const resultado = await compararComDocs();
      setComparacao(resultado);
      /* casados já vêm marcados (menos os que o Docs já dá como ligados --
         esses não precisam ser gravados de novo) -- mas nada vai pro Docs
         sem o clique em "Vincular selecionados" */
      const nossos = resultado.nossos || {};
      const marcados = new Set(
        (resultado.casados || [])
          .map(item => extrairLinha(item, nossos))
          .filter(l => l.vinculavel && !l.jaVinculado)
          .map(chaveLinha)
      );
      setSelecionados(marcados);
    } catch (e) { setErro(e.message); setComparacao(null); } finally { setComparando(false); }
  }

  function alternar(linha) {
    setSelecionados(prev => {
      const proximo = new Set(prev);
      const chaveDaLinha = chaveLinha(linha);
      if (proximo.has(chaveDaLinha)) proximo.delete(chaveDaLinha); else proximo.add(chaveDaLinha);
      return proximo;
    });
  }

  function alternarDocs(id) {
    setSelecionadosDocs(prev => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
      return proximo;
    });
  }

  /* Direção contrária: cliente que nasceu no Docs e não existe aqui. Cria
     o cadastro daqui e já fecha o par. Quem já existir aqui (mesmo código
     ou mesmo CNPJ) é pulado pelo backend, nunca sobrescrito. */
  async function handleCriarAqui() {
    const ids = [...selecionadosDocs];
    if (ids.length === 0) return;
    if (!window.confirm(`Cadastrar ${ids.length} cliente(s) aqui, a partir do Docs?\n\nEles entram como "pendente de validação" e já ficam vinculados. Nada é alterado no Docs além da ligação.`)) return;

    setCriandoAqui(true); limparAvisos();
    try {
      const r = await criarAquiDoDocs(ids);
      const partes = [`${(r.criados || []).length} cadastrado(s) aqui`];
      if (r.vinculados) partes.push(`${r.vinculados} vinculado(s)`);
      if ((r.pulados || []).length) partes.push(`${r.pulados.length} pulado(s)`);
      setSucesso(partes.join(' · ') + '.');
      if ((r.pulados || []).length) {
        setErro('Pulados: ' + r.pulados.map(p => `${p.codigo || p.docs_id} (${p.motivo})`).join('; '));
      }
      if (r.erro_vinculo) {
        setErro(`Cadastro feito aqui, mas o vínculo não fechou: ${r.erro_vinculo}. Rode "Comparar" — eles devem casar pelo código.`);
      }
      setSelecionadosDocs(new Set());
      await handleComparar();
    } catch (e) {
      setErro(e.message);
    } finally { setCriandoAqui(false); }
  }

  /* Os pares ligados saíram da fila de trabalho de propósito, mas sem
     poder vê-los um vínculo errado ficaria invisível. Carrega sob
     demanda, só quando alguém pede. */
  async function handleVerLigados() {
    if (ligados) { setLigados(null); return; }   // segundo clique fecha
    setCarregandoLigados(true); limparAvisos();
    try {
      setLigados(await listarLigados());
    } catch (e) { setErro(e.message); } finally { setCarregandoLigados(false); }
  }

  async function handleDesvincular(par) {
    const quem = par.codigo_aqui ? `${par.codigo_aqui} — ${par.nome_aqui}` : `${par.codigo_docs} — ${par.nome_docs}`;
    if (!window.confirm(`Desfazer o vínculo de ${quem}?\n\nNenhum cadastro é apagado dos dois lados — só a ligação. O par volta para a conferência.`)) return;
    limparAvisos();
    try {
      await desvincularNoDocs([par.docs_id]);
      setSucesso('Vínculo desfeito. O par volta na próxima comparação.');
      setLigados(await listarLigados());
    } catch (e) { setErro(e.message); }
  }

  function alternarNosso(id) {
    setSelecionadosNossos(prev => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
      return proximo;
    });
  }

  /* Só para o atrasado: no dia a dia o cliente novo já vai pro Docs
     sozinho, ao salvar o cadastro. Aqui há escolha a fazer -- nem todo
     cliente antigo precisa existir lá --, por isso vem desmarcado. */
  async function handleCriarNoDocs() {
    const ids = [...selecionadosNossos];
    if (ids.length === 0) return;
    if (!window.confirm(`Criar ${ids.length} cliente(s) no Docs?\n\nO Docs não altera nada que já exista lá: o que conflitar volta com o motivo, para alguém olhar.`)) return;

    setCriandoLa(true); limparAvisos();
    try {
      const r = await criarNoDocs(ids);
      const resultados = r.resultados || [];
      const conta = estado => resultados.filter(x => x.estado === estado).length;
      const partes = [];
      if (r.criados ?? conta('criado'))       partes.push(`${r.criados ?? conta('criado')} criado(s)`);
      if (r.vinculados ?? conta('vinculado')) partes.push(`${r.vinculados ?? conta('vinculado')} vinculado(s)`);
      setSucesso(partes.length ? partes.join(' · ') + ' no Docs.' : 'Nada foi criado no Docs.');

      /* o motivo vem escrito pra pessoa ler -- mostra como veio */
      const problemas = resultados.filter(x => x.estado === 'ja_existe' || x.estado === 'recusado');
      if (problemas.length) {
        setErro(problemas.map(p => `${p.codigo || p.nome}: ${p.motivo || p.estado}`).join(' | '));
      }
      setSelecionadosNossos(new Set());
      await handleComparar();
    } catch (e) {
      setErro(e.message);
    } finally { setCriandoLa(false); }
  }

  function marcarTodos(linhas, marcar) {
    setSelecionados(prev => {
      const proximo = new Set(prev);
      linhas.forEach(l => { if (marcar) proximo.add(chaveLinha(l)); else proximo.delete(chaveLinha(l)); });
      return proximo;
    });
  }

  const nossos   = comparacao?.nossos || {};
  const casados  = (comparacao?.casados  || []).map(i => extrairLinha(i, nossos));
  const conferir = (comparacao?.conferir || []).map(i => extrairLinha(i, nossos));
  /* Nomes sem ponto de vista: "aqui"/"lá" trocavam de significado conforme
     quem lia, e foi isso que inverteu os dois grupos na tela. Os nomes
     antigos (so_aqui/so_la) ainda saem da API, mas ficam só de reserva --
     atenção: so_aqui era o "só no Docs" e so_la era o "só aqui". */
  const soNoDocs = (comparacao?.so_no_docs ?? comparacao?.so_aqui ?? []).map(extrairRegistro);
  const soAqui   = (comparacao?.so_no_sistema_externo ?? comparacao?.so_la ?? []).map(extrairRegistro);

  /* `casados` passou a trazer só quem ainda falta vincular; quem já está
     ligado vira número em resumo.ja_vinculados (não volta como lista, pra
     não empilhar 91 linhas de "já ligado" na frente do que pede decisão). */
  const resumo       = comparacao?.resumo || {};
  const jaVinculados = resumo.ja_vinculados ?? casados.filter(l => l.jaVinculado).length;
  const inativos     = resumo.inativos ?? 0;
  const nadaADecidir = !!comparacao && casados.length === 0 && conferir.length === 0
    && soNoDocs.length === 0 && soAqui.length === 0;

  const selecionadasAgora = [...casados, ...conferir].filter(l => l.vinculavel && selecionados.has(chaveLinha(l)));
  const doGrupoConferir   = selecionadasAgora.filter(l => conferir.some(c => chaveLinha(c) === chaveLinha(l))).length;

  async function handleVincular() {
    if (selecionadasAgora.length === 0) return;
    const aviso = doGrupoConferir > 0
      ? `\n\nAtenção: ${doGrupoConferir} deles estão no grupo "conferir" (código e CNPJ não batem).`
      : '';
    if (!window.confirm(`Vincular ${selecionadasAgora.length} cliente(s) no Docs?${aviso}\n\nIsso grava só a ligação entre os cadastros. Nada é criado, renomeado ou apagado.`)) return;

    setVinculando(true); limparAvisos();
    try {
      const resultado = await vincularNoDocs(selecionadasAgora.map(montarPar));
      setSucesso(`${resultado.vinculados ?? selecionadasAgora.length} cliente(s) vinculado(s). Atualizando a conferência...`);
      setSelecionados(new Set());
      await handleComparar();
    } catch (e) {
      setErro(e.message);
    } finally { setVinculando(false); }
  }

  if (carregando) return <p className={styles.placeholderSub}>Carregando integração...</p>;

  const configurada = !!config?.configurada;
  const testadoEm = formatarDataHora(config?.ultimo_teste_em);

  return (
    <div className={styles.sistemaWrap}>
      <div ref={avisoRef}>
        {erro && <div className={styles.erroFaixa}><FiAlertCircle size={14} /> {erro}</div>}
        {sucesso && <div className={styles.sucessoFaixa}><FiCheckCircle size={14} /> {sucesso}</div>}
      </div>

      {/* ── Credencial ── */}
      <div className={styles.integracaoCard}>
        <div className={styles.integracaoCardHeader}>
          <div className={styles.integracaoCardTitulo}>
            <span className={styles.integracaoCardIcone}><FiLink size={14} /></span>
            Integração Docs
          </div>
          <span className={`${styles.statusBadge} ${configurada ? styles.statusConectado : styles.statusNaoConf}`}>
            {configurada
              ? <><FiCheckCircle size={12} /> Chave salva</>
              : <><FiAlertCircle size={12} /> Não configurado</>}
          </span>
        </div>

        <p className={styles.integracaoDesc}>
          Liga este app ao Docs (gestão de documentos) para o cadastro de clientes ser um só.
          A chave é gerada no Docs e identifica o escritório. Ela fica guardada cifrada e não volta a aparecer aqui depois de salva.
        </p>

        <div className={styles.campo}>
          <label className={styles.label}>Chave de integração</label>
          <div className={styles.chaveWrap}>
            <input
              type={mostrarChave ? 'text' : 'password'}
              className={styles.input}
              value={chave}
              onChange={e => setChave(e.target.value)}
              placeholder={configurada ? '••••••••••••  (já salva — preencha só para substituir)' : 'cole aqui a chave gerada no Docs'}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className={styles.btnOlho}
              onClick={() => setMostrarChave(v => !v)}
              title={mostrarChave ? 'Ocultar' : 'Mostrar enquanto digita'}
            >
              {mostrarChave ? <FiEyeOff size={15} /> : <FiEye size={15} />}
            </button>
          </div>
          <p className={styles.campoHint}>
            {configurada
              ? 'Uma chave já está salva. Preencha apenas para substituí-la.'
              : 'A chave só é enviada ao salvar e não fica gravada no navegador.'}
            {testadoEm && ` · Último teste: ${testadoEm} — ${config.ultimo_teste_ok ? 'ok' : 'falhou'}.`}
          </p>
        </div>

        <div className={styles.docsAcoes}>
          {configurada && (
            <button type="button" className={styles.btnCarregarModelos} onClick={handleRemoverChave} disabled={salvando}>
              <FiTrash2 size={13} /> Remover chave
            </button>
          )}
          <button
            type="button"
            className={styles.btnCarregarModelos}
            onClick={handleTestar}
            disabled={testando || !configurada}
            title={configurada ? 'Consulta o cadastro do Docs para conferir a chave' : 'Salve a chave primeiro'}
          >
            <FiRefreshCw size={13} className={testando ? styles.girando : ''} />
            {testando ? 'Testando...' : 'Testar conexão'}
          </button>
          <button type="button" className={styles.btnSalvar} onClick={handleSalvarChave} disabled={salvando || !chave.trim()}>
            <FiSave size={14} /> {salvando ? 'Salvando...' : 'Salvar chave'}
          </button>
        </div>

        {teste?.ok && (
          <div className={styles.docsResultadoTeste}>
            <FiCheckCircle size={14} /> Conexão ok — {teste.total} cliente(s) no Docs
            {typeof teste.ligados === 'number' && <> · {teste.ligados} já ligado(s) · {teste.nao_ligados} sem ligação</>}
          </div>
        )}
      </div>

      {/* ── Conferência ── */}
      <div className={styles.integracaoCard}>
        <div className={styles.integracaoCardHeader}>
          <div className={styles.integracaoCardTitulo}>
            <span className={styles.integracaoCardIcone}>⇄</span>
            Conferência de cadastros
          </div>
          <button
            type="button"
            className={styles.btnCarregarModelos}
            onClick={handleComparar}
            disabled={comparando || !configurada}
            title={configurada ? 'Compara os dois cadastros — não grava nada' : 'Salve a chave primeiro'}
          >
            <FiRefreshCw size={13} className={comparando ? styles.girando : ''} />
            {comparando ? 'Comparando...' : 'Comparar com o Docs'}
          </button>
        </div>

        <p className={styles.integracaoDesc}>
          A comparação não grava nada dos dois lados — pode rodar quantas vezes quiser.
          O vínculo só acontece quando você marca as linhas e clica em "Vincular selecionados".
        </p>

        {!comparacao && !comparando && (
          <p className={styles.docsVazio}>
            {configurada
              ? 'Clique em "Comparar com o Docs" para ver o que casa entre os dois cadastros.'
              : 'Salve a chave de integração para liberar a conferência.'}
          </p>
        )}

        {comparacao && (
          <>
            {/* Fila de trabalho: os números contam a história inteira, mas as
                listas abaixo só aparecem quando têm algo a decidir. */}
            <div className={styles.docsResumo}>
              {jaVinculados > 0 && (
                <span className={`${styles.docsChip} ${styles.docsChipOk}`}>{jaVinculados} já ligados</span>
              )}
              <span className={styles.docsChip}>{casados.length} a vincular</span>
              <span className={`${styles.docsChip} ${conferir.length ? styles.docsChipAtencao : ''}`}>
                {conferir.length} a conferir
              </span>
              {soNoDocs.length > 0 && <span className={styles.docsChip}>{soNoDocs.length} só no Docs</span>}
              {soAqui.length > 0 && <span className={styles.docsChip}>{soAqui.length} só aqui</span>}
              {inativos > 0 && <span className={styles.docsChipNeutro}>{inativos} inativos de fora</span>}
              {typeof comparacao.enviados === 'number' && (
                <span className={styles.docsChipNeutro}>{comparacao.enviados} enviados daqui</span>
              )}
              {typeof resumo.aqui === 'number' && (
                <span className={styles.docsChipNeutro}>
                  {resumo.aqui} ativos no Docs{typeof resumo.aqui_total === 'number' && resumo.aqui_total !== resumo.aqui ? ` (de ${resumo.aqui_total})` : ''}
                </span>
              )}
            </div>

            {nadaADecidir && (
              <div className={styles.docsResultadoTeste}>
                <FiCheckCircle size={14} /> Nada pendente: os dois cadastros estão conciliados.
              </div>
            )}

            {/* Os já ligados ficam fora da fila de trabalho, mas precisam
                poder ser vistos: é o único jeito de achar um vínculo errado. */}
            {jaVinculados > 0 && (
              <div className={styles.docsGrupo}>
                <button type="button" className={styles.docsGrupoHeader} onClick={handleVerLigados}>
                  <span className={styles.docsGrupoTitulo}>Pares já ligados</span>
                  <span className={styles.docsGrupoContagem}>{jaVinculados}</span>
                  <span className={styles.docsGrupoDesc}>
                    {carregandoLigados ? 'carregando...' : ligados ? 'clique para fechar' : 'abrir para conferir ou desfazer algum'}
                  </span>
                  <span className={styles.docsGrupoSeta}>{ligados ? '▾' : '▸'}</span>
                </button>
                {ligados && (
                  <>
                    {ligados.orfaos > 0 && (
                      <p className={styles.docsAvisoConferir}>
                        <FiAlertTriangle size={13} /> {ligados.orfaos} vínculo(s) apontam para um id que não é cliente daqui — desfaça e refaça pela conferência.
                      </p>
                    )}
                    <div className={styles.docsTabelaWrap}>
                      <table className={styles.docsTabela}>
                        <thead>
                          <tr>
                            <th>Código aqui</th>
                            <th>Nome aqui</th>
                            <th>Código no Docs</th>
                            <th>Nome no Docs</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(ligados.items || []).map(par => (
                            <tr key={par.docs_id} className={par.orfao ? styles.docsLinhaConferir : undefined}>
                              <td className={styles.docsCodigo}>{par.codigo_aqui || '—'}</td>
                              <td>{par.nome_aqui || <em className={styles.docsMotivo}>não é cliente deste app</em>}</td>
                              <td className={styles.docsCodigo}>{par.codigo_docs}</td>
                              <td>{par.nome_docs}</td>
                              <td>
                                <button type="button" className={styles.btnCarregarModelos} onClick={() => handleDesvincular(par)}>
                                  Desfazer
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {casados.length > 0 && (
              <Grupo
                titulo="A vincular"
                descricao="mesmo código nos dois lados — ligação segura"
                quantidade={casados.length}
              >
                <TabelaVinculavel
                  linhas={casados}
                  selecionados={selecionados}
                  onAlternar={alternar}
                  onMarcarTodos={marcarTodos}
                />
              </Grupo>
            )}

            {conferir.length > 0 && (
              <Grupo
                titulo="A conferir"
                descricao="código e CNPJ discordam — alguém precisa olhar antes"
                quantidade={conferir.length}
                tom="docsGrupoAtencao"
              >
                <>
                  <p className={styles.docsAvisoConferir}>
                    <FiAlertTriangle size={13} /> Estes vêm desmarcados de propósito. Marque um a um, só depois de conferir.
                  </p>
                  <TabelaVinculavel
                    linhas={conferir}
                    selecionados={selecionados}
                    onAlternar={alternar}
                    onMarcarTodos={marcarTodos}
                    destaque
                    mostrarMotivo
                  />
                </>
              </Grupo>
            )}

            {soNoDocs.length > 0 && (
              <Grupo
                titulo="Só no Docs"
                descricao="nasceram no Docs e ainda não existem aqui — marque para cadastrar"
                quantidade={soNoDocs.length}
              >
                <>
                  <TabelaInformativa
                    linhas={soNoDocs}
                    selecionados={selecionadosDocs}
                    onAlternar={alternarDocs}
                  />
                  <div className={styles.docsBarraVincular} style={{ padding: '0 14px 14px' }}>
                    <span className={styles.docsSelecaoInfo}>
                      {selecionadosDocs.size} selecionado(s) — entram como “pendente de validação”
                    </span>
                    <button
                      type="button"
                      className={styles.btnSalvar}
                      onClick={handleCriarAqui}
                      disabled={criandoAqui || selecionadosDocs.size === 0}
                    >
                      <FiPlus size={14} /> {criandoAqui ? 'Cadastrando...' : 'Cadastrar aqui'}
                    </button>
                  </div>
                </>
              </Grupo>
            )}

            {soAqui.length > 0 && (
              <Grupo
                titulo="Só aqui (app.essensials)"
                descricao="cadastros antigos que ainda não existem no Docs — inativos já ficam de fora"
                quantidade={soAqui.length}
              >
                <>
                  <p className={styles.docsAvisoConferir} style={{ color: '#6b7f99', background: '#f8fbff', borderBottomColor: '#e4edf5' }}>
                    <FiInfo size={13} /> Cliente novo vai para o Docs sozinho, ao salvar o cadastro. Esta lista é o atrasado — marque só quem deve existir lá.
                  </p>
                  <TabelaInformativa
                    linhas={soAqui}
                    selecionados={selecionadosNossos}
                    onAlternar={alternarNosso}
                  />
                  <div className={styles.docsBarraVincular} style={{ padding: '0 14px 14px' }}>
                    <span className={styles.docsSelecaoInfo}>{selecionadosNossos.size} selecionado(s)</span>
                    <button
                      type="button"
                      className={styles.btnSalvar}
                      onClick={handleCriarNoDocs}
                      disabled={criandoLa || selecionadosNossos.size === 0}
                    >
                      <FiPlus size={14} /> {criandoLa ? 'Criando...' : 'Criar no Docs'}
                    </button>
                  </div>
                </>
              </Grupo>
            )}

            <p className={styles.docsNota}>
              <FiInfo size={12} /> Integração nunca apaga cadastro: quem sai de cena vira inativo.
              Cadastrar aqui um cliente do Docs não altera nada lá além da ligação.
            </p>

            {(casados.length > 0 || conferir.length > 0) && (
            <div className={styles.docsBarraVincular}>
              <span className={styles.docsSelecaoInfo}>
                {selecionadasAgora.length} selecionado(s)
                {doGrupoConferir > 0 && <strong> · {doGrupoConferir} do grupo "a conferir"</strong>}
              </span>
              <button
                type="button"
                className={styles.btnSalvar}
                onClick={handleVincular}
                disabled={vinculando || selecionadasAgora.length === 0}
              >
                <FiLink size={14} /> {vinculando ? 'Vinculando...' : 'Vincular selecionados'}
              </button>
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DocsTab;
